import { PageLoader } from "@/components/ui";
import { ORG_BILLING_ENABLED } from "@/lib/featureFlags";
import { ChatProvider } from "@/contexts/ChatContext";
import { AuthLayout, ChatLayout, LandingLayout, MainLayout } from "@/layouts";
import AuthCallback from "@/pages/auth/AuthCallback";
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import ChatPage from "@/pages/chat/ChatPage";
import LandingPage from "@/pages/landing/LandingPage";
import SingleWorkspacePage from "@/pages/workspaces/SingleWorkspacePage";
import useAuthStore from "@/store/authStore";
import PropTypes from "prop-types";
import { lazy, Suspense, useEffect, useState, useCallback } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";

// Retry wrapper for dynamic imports with exponential backoff
const retryImport = async (importFn, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await importFn();
    } catch (error) {
      if (i === retries - 1) throw error;
      // Exponential backoff: 1s, 2s, 4s
      await new Promise((resolve) =>
        setTimeout(resolve, delay * Math.pow(2, i))
      );
    }
  }
};

// Check if error is a chunk loading failure (usually from new deployment)
const isChunkLoadError = (error) => {
  const message = error?.message || "";
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk") ||
    message.includes("error loading dynamically imported module") ||
    error?.name === "ChunkLoadError"
  );
};

// Error component with retry button
const LazyLoadError = ({ displayName, onRetry }) => {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] p-8 text-center">
      <p className="text-muted-foreground mb-2">
        Failed to load {displayName}.
      </p>
      <p className="text-sm text-muted-foreground mb-4">
        A new version may have been deployed. Please refresh to get the latest
        version.
      </p>
      <button
        onClick={handleRetry}
        disabled={isRetrying}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {isRetrying ? "Refreshing..." : "Refresh Page"}
      </button>
    </div>
  );
};

LazyLoadError.propTypes = {
  displayName: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export const lazyLoad = (importFn, displayName = "Component") => {
  const LazyComponent = lazy(() => {
    return retryImport(importFn).catch((error) => {
      console.error(`Error loading ${displayName}:`, error);
      return {
        default: () => (
          <LazyLoadError
            displayName={displayName}
            onRetry={() => window.location.reload()}
          />
        ),
      };
    });
  });

  LazyComponent.displayName = displayName;

  const SuspenseWrapper = (props) => (
    <Suspense fallback={<PageLoader message={`Loading ${displayName}...`} />}>
      <LazyComponent {...props} />
    </Suspense>
  );

  SuspenseWrapper.displayName = `${displayName}Wrapper`;

  return SuspenseWrapper;
};
lazyLoad.displayName = "lazyLoad";

// Static file redirect component
const StaticFileRedirect = ({ filePath }) => {
  useEffect(() => {
    window.location.href = filePath;
  }, [filePath]);

  return <PageLoader message={`Loading ${filePath}...`} />;
};

StaticFileRedirect.propTypes = {
  filePath: PropTypes.string.isRequired,
};

// Optimized loading wrapper component to reduce redundancy
const LoadingRoute = ({
  component: Component,
  loadingMessage = "Loading...",
  props: componentProps = {},
  ...rest
}) => (
  <Suspense fallback={<PageLoader message={loadingMessage} />}>
    <Component {...componentProps} {...rest} />
  </Suspense>
);

LoadingRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  loadingMessage: PropTypes.string,
  props: PropTypes.object,
};

// Import pages with optimized lazy loading
const AboutPage = lazyLoad(() => import("@/pages/about"));
const BrandPage = lazyLoad(() => import("@/pages/brand"));
const FeaturesPage = lazyLoad(() => import("@/pages/features"));
const SolutionsPage = lazyLoad(() => import("@/pages/solutions"));
const IntegrationsPage = lazyLoad(() => import("@/pages/integrations"));
const SharedChatPage = lazyLoad(() => import("@/pages/chat/SharedChatPage"));
const ForgotPasswordPage = lazyLoad(
  () => import("@/pages/auth/ForgotPasswordPage")
);
const ResetPasswordPage = lazyLoad(
  () => import("@/pages/auth/ResetPasswordPage")
);
const OnboardingPage = lazyLoad(() => import("@/pages/auth/OnboardingPage"));
const ChangelogPage = lazyLoad(() => import("@/pages/ChangelogPage"));
const NotFoundPage = lazyLoad(() => import("@/pages/error/NotFoundPage"));

// Test pages - only loaded in development
const OnboardingTestPage = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/test/OnboardingTestPage"))
  : null;
const ErrorDisplayTestPage = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/test/ErrorDisplayTestPage"))
  : null;
const TestIndexPage = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/test/TestIndexPage"))
  : null;
const ButtonContrastTest = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/test/ButtonContrastTest"))
  : null;
const TempChatTestPage = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/test/temp-chat/TempChatTestPage"))
  : null;
const ModelHealthPage = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/test/ModelHealthPage"))
  : null;
const DevLoginPage = import.meta.env.DEV
  ? lazyLoad(() => import("@/pages/auth/DevLoginPage"))
  : null;
const CheckoutPage = lazyLoad(() => import("@/pages/checkout"));

// Org pages - only loaded when org billing feature flag is enabled
const OrgBillingPage = lazyLoad(() => import("@/pages/org/OrgBillingPage"));
const OrgMembersPage = lazyLoad(() => import("@/pages/org/OrgMembersPage"));
const OrgSettingsPage = lazyLoad(() => import("@/pages/org/OrgSettingsPage"));
const AcceptInvitePage = lazyLoad(() => import("@/pages/org/AcceptInvitePage"));
const ArticlePage = lazyLoad(() => import("@/pages/news/ArticlePage"));
const PrivacyPolicyPage = lazyLoad(() => import("@/pages/privacy"));
const TermsPage = lazyLoad(() => import("@/pages/terms"));
const FAQsPage = lazyLoad(() => import("@/pages/faqs"));
const FAQTopicPage = lazyLoad(() => import("@/pages/faqs/FAQTopicPage"));
const SupportPage = lazyLoad(() => import("@/pages/support"));
const LibraryPage = lazyLoad(() => import("@/pages/library/LibraryPage"));
const ImageLightboxPage = lazyLoad(
  () => import("@/pages/library/ImageLightboxPage")
);
const CanvasesPage = lazyLoad(
  () => import("@/pages/canvases/CanvasesPage"),
  "Documents"
);
const SharedCanvasPage = lazyLoad(
  () => import("@/pages/canvases/SharedCanvasPage")
);
const AdminDashboard = lazyLoad(
  () => import("@/pages/admin/AdminDashboard"),
  "AdminDashboard"
);
const SettingsPage = lazyLoad(
  () => import("@/pages/SettingsPage"),
  "SettingsPage"
);
const OAuthRedirectHandler = ({ children }) => {
  return children;
};
const NewsPage = lazyLoad(() => import("@/pages/news/NewsPage"));
const Solution = lazyLoad(
  () => import("@/pages/solutions/components/Solution")
);
const TempChatPage = lazyLoad(
  () => import("@/pages/temp-chat/TempChat2Page"),
  "TempChat"
);

OAuthRedirectHandler.propTypes = {
  children: PropTypes.node.isRequired,
};

const ProtectedLayout = () => {
  return (
    <ProtectedRoute>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  );
};

// Root path handler that checks for OAuth code parameters and user auth status
const RootPathHandler = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const oauthCode = urlParams.get("code");
  // Get auth store state directly, not as function call
  const authState = useAuthStore();
  // Safely check if isAuthenticated is a function, then call it, or fall back to checking user directly
  const isAuthenticated =
    typeof authState.isAuthenticated === "function"
      ? authState.isAuthenticated()
      : !!authState.user;
  const profile = authState.profile;
  const initialized = authState.initialized;
  const isLoading = authState.isLoading;
  const initialize = authState.initialize;

  useEffect(() => {
    // Initialize auth state if not already initialized
    if (!initialized && !isLoading) {
      initialize();
    }
  }, [initialized, isLoading, initialize]);

  useEffect(() => {
    if (oauthCode) {
      // console.log("Detected OAuth code parameter at root path:", oauthCode);
      window.location.href = `/auth/callback${location.search}`;
      return;
    }
  }, [oauthCode, location.search]);

  // Show loading state while auth is initializing
  if (!initialized || isLoading) {
    return <PageLoader message="Loading..." />;
  }

  // Handle OAuth redirect first
  if (oauthCode) {
    return <PageLoader message="Processing authentication..." />;
  }

  // If user is authenticated with a profile, immediately redirect to chat
  if (initialized && isAuthenticated && profile) {
    return <Navigate to="/chat" replace />;
  }

  // Show landing page if user is not authenticated or has no profile
  return <LandingPage />;
};

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <Outlet />,
      children: [
        {
          path: "/",
          element: <LandingLayout />,
          children: [
            {
              path: "",
              element: <RootPathHandler />,
            },
            {
              path: "about",
              element: (
                <LoadingRoute
                  component={AboutPage}
                  loadingMessage="Loading about page..."
                />
              ),
            },
            {
              path: "brand",
              element: (
                <LoadingRoute
                  component={BrandPage}
                  loadingMessage="Loading brand guidelines..."
                />
              ),
            },
            {
              path: "solutions",
              children: [
                {
                  path: "",
                  element: (
                    <LoadingRoute
                      component={SolutionsPage}
                      loadingMessage="Loading solutions..."
                    />
                  ),
                },
                {
                  path: ":solutionId",
                  element: (
                    <LoadingRoute
                      component={Solution}
                      loadingMessage="Loading solution..."
                    />
                  ),
                },
              ],
            },
            {
              path: "integrations",
              element: (
                <LoadingRoute
                  component={IntegrationsPage}
                  loadingMessage="Loading integrations..."
                />
              ),
            },
            {
              path: "features",
              element: (
                <LoadingRoute
                  component={FeaturesPage}
                  loadingMessage="Loading features..."
                />
              ),
            },
            {
              path: "changelog",
              element: (
                <LoadingRoute
                  component={ChangelogPage}
                  loadingMessage="Loading changelog..."
                />
              ),
            },
            {
              path: "docs",
              children: [
                {
                  path: "",
                  element: (
                    <LoadingRoute
                      component={FAQsPage}
                      loadingMessage="Loading documentation..."
                    />
                  ),
                },
                {
                  path: ":topicId",
                  element: (
                    <LoadingRoute
                      component={FAQTopicPage}
                      loadingMessage="Loading documentation..."
                    />
                  ),
                },
              ],
            },
            {
              // Redirect old /faqs routes to /docs
              path: "faqs",
              element: <Navigate to="/docs" replace />,
            },
            {
              path: "faqs/:topicId",
              element: <Navigate to="/docs/:topicId" replace />,
            },
            {
              path: "reset-password",
              element: <Navigate to="/auth/reset-password" replace />,
            },
            {
              path: "privacy",
              element: (
                <LoadingRoute
                  component={PrivacyPolicyPage}
                  loadingMessage="Loading privacy policy..."
                />
              ),
            },
            {
              path: "terms",
              element: (
                <LoadingRoute
                  component={TermsPage}
                  loadingMessage="Loading terms and conditions..."
                />
              ),
            },
            {
              path: "support",
              element: (
                <LoadingRoute
                  component={SupportPage}
                  loadingMessage="Loading support page..."
                />
              ),
            },
            {
              path: "pricing",
              element: (
                <LoadingRoute
                  component={CheckoutPage}
                  loadingMessage="Loading pricing..."
                />
              ),
            },
            // Org routes - only available when org billing is enabled
            ...(ORG_BILLING_ENABLED
              ? [
                  {
                    path: "org",
                    children: [
                      {
                        path: "accept-invite/:token",
                        element: (
                          <LoadingRoute
                            component={AcceptInvitePage}
                            loadingMessage="Loading invitation..."
                          />
                        ),
                      },
                      {
                        path: ":orgId/billing",
                        element: (
                          <LoadingRoute
                            component={OrgBillingPage}
                            loadingMessage="Loading billing..."
                          />
                        ),
                      },
                      {
                        path: ":orgId/members",
                        element: (
                          <LoadingRoute
                            component={OrgMembersPage}
                            loadingMessage="Loading members..."
                          />
                        ),
                      },
                      {
                        path: ":orgId/settings",
                        element: (
                          <LoadingRoute
                            component={OrgSettingsPage}
                            loadingMessage="Loading settings..."
                          />
                        ),
                      },
                    ],
                  },
                ]
              : []),
            {
              path: "news",
              children: [
                {
                  path: "",
                  element: <NewsPage />,
                },
                {
                  path: ":articleId",
                  element: (
                    <LoadingRoute
                      component={lazyLoad(
                        () => import("@/pages/news/ArticlePage")
                      )}
                      loadingMessage="Loading article..."
                    />
                  ),
                },
              ],
            },
          ],
        },
        {
          path: "/share",
          children: [
            {
              path: "chat/:threadId",
              element: (
                <LoadingRoute
                  component={SharedChatPage}
                  loadingMessage="Loading shared chat..."
                />
              ),
            },
            {
              path: "canvas/:messageId",
              element: (
                <LoadingRoute
                  component={SharedCanvasPage}
                  loadingMessage="Loading shared canvas..."
                />
              ),
            },
          ],
        },
        {
          path: "/temp-chat",
          element: (
            <LoadingRoute
              component={TempChatPage}
              loadingMessage="Loading temporary chat..."
            />
          ),
        },
        {
          path: "/auth",
          element: <AuthLayout />,
          children: [
            {
              path: "",
              element: <Navigate to="/auth/login" replace />,
            },
            {
              path: "login",
              element: <LoginPage />,
            },
            {
              path: "signup",
              element: <SignupPage />,
            },
            {
              path: "callback",
              element: <AuthCallback />,
            },
            {
              path: "forgot-password",
              element: (
                <LoadingRoute
                  component={ForgotPasswordPage}
                  loadingMessage="Loading..."
                />
              ),
            },
            {
              path: "reset-password",
              element: (
                <LoadingRoute
                  component={ResetPasswordPage}
                  loadingMessage="Loading password reset..."
                />
              ),
            },
            {
              path: "onboarding",
              children: [
                {
                  path: "",
                  element: (
                    <LoadingRoute
                      component={OnboardingPage}
                      loadingMessage="Loading onboarding..."
                    />
                  ),
                },
                {
                  path: "profile",
                  element: (
                    <LoadingRoute
                      component={OnboardingPage}
                      props={{ step: "profile" }}
                      loadingMessage="Loading profile setup..."
                    />
                  ),
                },
                {
                  path: "interests",
                  element: (
                    <LoadingRoute
                      component={OnboardingPage}
                      props={{ step: "interests" }}
                      loadingMessage="Loading interests selection..."
                    />
                  ),
                },
                {
                  path: "instructions",
                  element: (
                    <LoadingRoute
                      component={OnboardingPage}
                      props={{ step: "instructions" }}
                      loadingMessage="Loading custom instructions..."
                    />
                  ),
                },
                {
                  path: "subscription",
                  element: (
                    <LoadingRoute
                      component={OnboardingPage}
                      props={{ step: "subscription" }}
                      loadingMessage="Loading subscription options..."
                    />
                  ),
                },
              ],
            },
          ],
        },
        {
          element: <ProtectedLayout />,
          children: [
            {
              path: "dashboard",
              element: (
                <LoadingRoute
                  component={AdminDashboard}
                  loadingMessage="Loading admin dashboard..."
                />
              ),
            },
            {
              path: "settings",
              element: (
                <LoadingRoute
                  component={SettingsPage}
                  loadingMessage="Loading settings..."
                />
              ),
            },
            {
              path: "documentation",
              element: <Navigate to="/docs" replace />,
            },
            {
              path: "library",
              children: [
                {
                  path: "",
                  element: (
                    <LoadingRoute
                      component={LibraryPage}
                      loadingMessage="Loading library..."
                    />
                  ),
                },
                {
                  path: "image/:imageId",
                  element: (
                    <LoadingRoute
                      component={ImageLightboxPage}
                      loadingMessage="Loading image..."
                    />
                  ),
                },
              ],
            },
            {
              path: "documents",
              element: (
                <LoadingRoute
                  component={CanvasesPage}
                  loadingMessage="Loading documents..."
                />
              ),
            },
            {
              path: "projects",
              element: <Outlet />,
              children: [
                {
                  path: ":workspaceId",
                  element: (
                    <ChatProvider>
                      <SingleWorkspacePage />
                    </ChatProvider>
                  ),
                  children: [
                    {
                      path: "chat",
                      element: <ChatPage />,
                      children: [
                        {
                          path: "",
                          element: <ChatPage />,
                        },
                        {
                          path: "new",
                          element: <ChatPage />,
                        },
                        {
                          path: ":chatId",
                          element: <ChatPage />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: "chat",
              element: <ChatLayout />,
              children: [
                {
                  path: "",
                  element: <Navigate to="/chat/new" replace />,
                },
                {
                  path: "new",
                  element: <ChatPage isNewChat={true} />,
                },
                {
                  path: ":chatId",
                  element: <ChatPage />,
                },
                {
                  path: "tool/:toolId",
                  element: <ChatPage />,
                },
              ],
            },
          ],
        },
        // Sitemap route - serves the static file directly
        {
          path: "/sitemap.xml",
          element: <StaticFileRedirect filePath="/sitemap.xml" />,
        },
        {
          path: "/robots.txt",
          element: <StaticFileRedirect filePath="/robots.txt" />,
        },
        // Test routes - only available in development
        ...(import.meta.env.DEV
          ? [
              {
                path: "/test",
                element: (
                  <LoadingRoute
                    component={TestIndexPage}
                    loadingMessage="Loading test index..."
                  />
                ),
              },
              {
                path: "/test/onboarding",
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: (
                      <LoadingRoute
                        component={OnboardingTestPage}
                        loadingMessage="Loading test hub..."
                      />
                    ),
                  },
                  {
                    path: "profile",
                    element: (
                      <LoadingRoute
                        component={OnboardingTestPage}
                        props={{ step: "profile" }}
                        loadingMessage="Loading profile test..."
                      />
                    ),
                  },
                  {
                    path: "interests",
                    element: (
                      <LoadingRoute
                        component={OnboardingTestPage}
                        props={{ step: "interests" }}
                        loadingMessage="Loading interests test..."
                      />
                    ),
                  },
                  {
                    path: "instructions",
                    element: (
                      <LoadingRoute
                        component={OnboardingTestPage}
                        props={{ step: "instructions" }}
                        loadingMessage="Loading instructions test..."
                      />
                    ),
                  },
                  {
                    path: "subscription",
                    element: (
                      <LoadingRoute
                        component={OnboardingTestPage}
                        props={{ step: "subscription" }}
                        loadingMessage="Loading subscription test..."
                      />
                    ),
                  },
                ],
              },
              {
                path: "/test/errors",
                element: (
                  <LoadingRoute
                    component={ErrorDisplayTestPage}
                    loadingMessage="Loading error display test..."
                  />
                ),
              },
              {
                path: "/test/button-contrast",
                element: (
                  <LoadingRoute
                    component={ButtonContrastTest}
                    loadingMessage="Loading button contrast test..."
                  />
                ),
              },
              {
                path: "/test/temp-chat",
                element: (
                  <LoadingRoute
                    component={TempChatTestPage}
                    loadingMessage="Loading temp chat test..."
                  />
                ),
              },
              {
                path: "/test/model-health",
                element: (
                  <LoadingRoute
                    component={ModelHealthPage}
                    loadingMessage="Loading model health check..."
                  />
                ),
              },
              {
                path: "/test/dev-login",
                element: <AuthLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <LoadingRoute
                        component={DevLoginPage}
                        loadingMessage="Loading dev login..."
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
        {
          path: "*",
          element: <NotFoundPage />,
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
    // Disable automatic scroll restoration to prevent back button scroll issues
    // We'll handle scroll management manually
    scrollRestoration: "manual",
  }
);

export default router;
